import { LessonProblem } from '@yandex-int/k-common/typings';
import beminize from 'bem-css-modules';
import cx from 'classnames';
import { isNil } from 'lodash';
import { getWizardClassName } from 'platform-components/src/components/wizard/utils';
import { mediaQueryResolver, SCREEN_SIZE, WithMediaQueryResolverProps } from 'platform-components/hocs';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ReactSortable, Sortable } from 'react-sortablejs';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import * as problemsActions from 'store/problems/actions';

//@ts-ignore
import BREAKPOINTS from '../../design/breakpoints.scss';
import * as actions from '../lesson-view.store/actions';

import Problem from './__problem/problems__problem';
import { LessonViewProblemsStub } from './__stub/problems__stub';
import { SCREEN_QUERY } from './constants';
//@ts-ignore
import styles from './problems.module.scss';
import * as selectors from './selectors';

const b = beminize(styles, 'problems');

interface StoreProps {
    canShowProblemsContent: boolean;
    courseId: number | null;
    isAfterCopyingAsRNO: boolean;
    problems: Array<LessonProblem>;
    problemsCount: Record<number, number>;
    shouldShowUsages: boolean;
}

interface HOCProps extends WithMediaQueryResolverProps {}

interface DispatchProps {
    moveProblems: typeof lessonActions.moveProblems;
    saveLesson: typeof lessonActions.saveLesson;
    getProblemsInCourseRequest: typeof problemsActions.getProblemsInCourseRequest;
    getProblemStacksRequest: typeof actions.getProblemStacksRequest;
}

interface OwnProps {
    canEdit: boolean;
    mix?: string;
}

export interface Props extends OwnProps, StoreProps, DispatchProps, HOCProps {}

export const Problems = (props: Props) => {
    const {
        canEdit,
        canShowProblemsContent,
        problems,
        isAfterCopyingAsRNO,
        mix,
        moveProblems,
        saveLesson,
        getProblemsInCourseRequest,
        courseId,
        shouldShowUsages,
        problemsCount,
        screenSize,
        getProblemStacksRequest,
    } = props;

    // временное решение: для маленьких экранов отключаем перетаскивание карточек,
    // так как на мобилках драг-н-дроп контаминируется со скроллом.
    const isSmallScreen = Boolean(screenSize && screenSize === SCREEN_SIZE.SMALL);
    const shouldDisableSortable = !canEdit || isSmallScreen;

    const [list, setList] = useState(getProblemIds(problems));

    useEffect(() => {
        setList(getProblemIds(problems));
    }, [problems]);

    useEffect(() => {
        if (courseId !== null && shouldShowUsages) {
            const problemsIds = getProblemIds(problems).map(({ id }) => id);
            getProblemsInCourseRequest(problemsIds, courseId);
        }
    }, [courseId, getProblemsInCourseRequest, problems, shouldShowUsages]);

    useEffect(() => {
        getProblemStacksRequest();
    }, [getProblemStacksRequest]);

    const handleSort = (event: Sortable.SortableEvent) => {
        const { oldIndex, newIndex } = event;

        if (!isNil(oldIndex) && !isNil(newIndex)) {
            moveProblems({ oldIndex, newIndex });
            saveLesson();

            ym('reachGoal', 'teacher_lesson_creation_reordered_cards', {
                type: 'dragndrop',
            });
        }
    };

    const accumulator: LessonProblem[] = [];
    const items = list.reduce((result, { id: problemId }) => {
        const lessonProblem = problems.find((problem) => problem.problem?.id === problemId);

        if (lessonProblem?.problem?.id) {
            result.push(lessonProblem);
        }

        return result;
    }, accumulator);

    const getItems = (): Array<React.ReactNode> => {
        const problemMix = isAfterCopyingAsRNO ? getWizardClassName('problems__problem') : '';

        return items.map((problem, index) => (
            <Problem
                key={problem.id}
                mix={problemMix}
                problem={problem!}
                problemIndex={index}
                shouldShowTag={false}
                usagesCount={problemsCount[problem.id] || null}
            />
        ));
    };

    if (!canShowProblemsContent) {
        return <LessonViewProblemsStub />;
    }

    // react-sortable не обрабатывает смену состояния disabled,
    // а shouldDisableSortable меняется динамически при ресайзе
    if (shouldDisableSortable) {
        return <div className={cx(b(), 'qa_lesson-view_problems', mix)}>{getItems()}</div>;
    }

    return (
        /* @ts-ignore - EDUCATION-37077: [react-update]: fixme typings: обновить до совместимого по тайпингам с react18 */
        <ReactSortable
            animation={200}
            className={cx(b(), 'qa_lesson-view_problems')}
            delay={4}
            delayOnTouchOnly={true}
            ghostClass={b('ghost')}
            list={list}
            onEnd={handleSort}
            setList={(newList) => setList(newList)}
        >
            {getItems()}
        </ReactSortable>
    );
};

function getProblemIds(problems: LessonProblem[]): Array<{ id: number }> {
    const accumulator: Array<{ id: number }> = [];
    return problems.reduce((result, lessonProblem) => {
        if (lessonProblem.problem) {
            result.push({ id: lessonProblem.problem!.id });
        }

        return result;
    }, accumulator);
}

const mapStateToProps = (storeState: any): StoreProps => {
    return {
        canShowProblemsContent: lessonSelectors.getCanShowProblemsContent(storeState),
        problems: selectors.getLoadedLessonProblems(storeState),
        problemsCount: selectors.getLessonProblemsInCourseCount(storeState),
        shouldShowUsages: selectors.getShouldShowUsages(storeState),
        courseId: lessonSelectors.getLessonOrCurrentYearCourseId(storeState),
        isAfterCopyingAsRNO: lessonViewSelectors.getIsAfterCopyingAsRNO(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            moveProblems: lessonActions.moveProblems,
            saveLesson: lessonActions.saveLesson,
            getProblemsInCourseRequest: problemsActions.getProblemsInCourseRequest,
            getProblemStacksRequest: actions.getProblemStacksRequest,
        },
        dispatch
    );
};

const connected = mediaQueryResolver(
    [],
    BREAKPOINTS,
    SCREEN_QUERY
)(connect(mapStateToProps, mapDispatchToProps)(Problems)) as React.ComponentType<OwnProps>;
connected.displayName = 'LessonViewProblems';

export default connected;
